import { useRef, useState } from 'react'
import { looksBlurry, prepareFile, type CapturedPage } from '@core/capture'
import { queueSend } from '@core/documents'
import type { FamilySession } from '@core/session'
import { Button, Card, ErrorNote } from '@design/components'
import { Overlay } from '@design/Overlay'

/**
 * Adding a document yourself — a photo, or a PDF that arrived by email.
 *
 * The parents' send flow opens the camera and asks nothing (rule 20). This is
 * the administrator's own version of it, and it differs in exactly two ways:
 * you can pick a file rather than only take a picture, and you can say which
 * space it belongs to instead of letting supplier history guess. Both because
 * you already know the answer, and being made to wait for a guess you could
 * have typed is its own kind of friction.
 *
 * A PDF is uploaded untouched. That matters more than it sounds: Azure reads a
 * PDF's text layer exactly, so the amount and the IBAN come out right, where a
 * photograph of the same invoice is an OCR problem.
 */
export function UploadScreen({
  session,
  onDone,
  onClose,
}: {
  session: FamilySession
  onDone: () => void
  onClose: () => void
}) {
  const [pages, setPages] = useState<CapturedPage[]>([])
  const [spaceId, setSpaceId] = useState(session.spaces[0]?.id ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  async function onFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (files.length === 0) return

    setBusy(true)
    setError('')
    const accepted: CapturedPage[] = []
    const rejected: string[] = []
    for (const file of files) {
      try {
        accepted.push(await prepareFile(file))
      } catch (err) {
        // One unreadable file must not throw away the others someone just
        // spent a minute selecting.
        rejected.push(err instanceof Error ? err.message : String(err))
      }
    }
    setBusy(false)
    setPages((prev) => [...prev, ...accepted])
    if (rejected.length > 0) setError(rejected.join(' '))
  }

  async function send() {
    setBusy(true)
    try {
      await queueSend({
        spaceId,
        uploadedBy: session.userId,
        pages: pages.map((p) => p.blob),
        source: 'file',
      })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uploaden lukte niet.')
      setBusy(false)
    }
  }

  const blurry = pages.some(looksBlurry)

  return (
    <Overlay>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        multiple
        onChange={onFiles}
        className="hidden"
      />

      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <button onClick={onClose} className="text-muted px-2 py-2">
          Annuleren
        </button>
        <span className="font-medium">Factuur toevoegen</span>
        <span className="w-20" />
      </header>

      <div className="flex-1 scroll-area px-4 py-4">
        {session.spaces.length > 1 && (
          <>
            <p className="text-sm text-muted mb-1.5">Waarvoor is dit?</p>
            <div className="flex gap-2 mb-4">
              {session.spaces.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSpaceId(s.id)}
                  className={`flex-1 min-h-[3rem] rounded-xl border text-sm font-medium transition-colors ${
                    s.id === spaceId ? 'border-ink bg-ink/5 text-ink' : 'border-line text-muted'
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </>
        )}

        {pages.length === 0 && !busy && (
          <Card className="text-center py-8 mb-4">
            <p className="font-medium mb-1">Kies een foto of pdf</p>
            <p className="text-sm text-muted">
              Meerdere pagina's mag ook. Een pdf wordt het best gelezen.
            </p>
          </Card>
        )}

        {busy && pages.length === 0 && <p className="text-muted text-center py-6">Even geduld…</p>}

        {blurry && (
          <Card className="mb-4 border-warn/40 bg-warn/5">
            <p className="text-sm">
              Een van de foto's is wat wazig. Versturen mag gerust — het lukt
              meestal toch.
            </p>
          </Card>
        )}

        <div className="space-y-2">
          {pages.map((page, i) => (
            <Card key={i} className="flex items-center gap-3">
              <span className="shrink-0 w-10 h-10 rounded-lg bg-line grid place-items-center text-[0.65rem] font-medium text-muted">
                {page.contentType === 'application/pdf' ? 'PDF' : i + 1}
              </span>
              <span className="flex-1 min-w-0 truncate text-sm">
                {page.name || `Pagina ${i + 1}`}
              </span>
              <button
                onClick={() => setPages((p) => p.filter((_, j) => j !== i))}
                className="text-sm text-muted px-2 py-1"
              >
                Weg
              </button>
            </Card>
          ))}
        </div>

        <ErrorNote>{error}</ErrorNote>
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line space-y-2">
        <Button tone="secondary" onClick={() => inputRef.current?.click()} disabled={busy}>
          {pages.length === 0 ? 'Bestand kiezen' : 'Nog een pagina'}
        </Button>
        <Button size="large" onClick={send} disabled={busy || pages.length === 0 || !spaceId}>
          {busy ? 'Bezig…' : 'Toevoegen'}
        </Button>
      </div>
    </Overlay>
  )
}
